import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MessageCircle } from "lucide-react";
import { CONTACT } from "@/data";
import { ROUTES } from "@/legal";
import { useI18n } from "@/i18n";
import { Reveal } from "./motion";
import { RequestCallModal } from "./RequestCallModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[#5c5c57]">{label}</span>
    <input
      {...props}
      className="mt-3 w-full border-0 border-b border-[#3a3a37] bg-transparent pb-3 font-display text-xl text-[#f4f4f0] outline-none transition-colors placeholder:text-[#5c5c57] focus:border-[#9e8d7b] md:text-2xl"
    />
  </label>
);

export const Contact = () => {
  const { t } = useI18n();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const rc = t.requestCall;
  const waHref = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(rc.whatsappMsg)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.okToast);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(c.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#1c1c1a] py-28 text-[#f4f4f0] md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-body text-[11px] uppercase tracking-[0.28em] text-[#9e8d7b]">{c.label}</span>
              <h2 className="mt-6 font-display text-5xl leading-[0.95] tracking-tight md:text-7xl">
                {c.headingA}
                <br />
                {c.headingB} <span className="italic text-[#9e8d7b]">{c.headingC}</span>
              </h2>
              <p className="mt-8 max-w-sm font-body text-base leading-relaxed text-[#b8b8b2]">{c.introA}<br />{c.introB}</p>

              <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center">
                <RequestCallModal />
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-button"
                  className="inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em] text-[#8a8a83] transition-colors hover:text-[#f4f4f0]"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  {rc.whatsapp}
                </a>
              </div>

              <div className="mt-12 space-y-5">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="link-underline block font-display text-2xl text-[#f4f4f0]">
                  {CONTACT.email}
                </a>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="link-underline block font-display text-2xl text-[#f4f4f0]">
                  {CONTACT.phone}
                </a>
                <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#5c5c57]" data-testid="contact-address">
                  {CONTACT.address}
                </p>
                <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#5c5c57]">{c.city}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-10">
                <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                  <Field label={c.name} data-testid="contact-name-input" autoComplete="name" value={form.name} onChange={set("name")} placeholder={c.namePh} />
                  <Field label={c.email} type="email" data-testid="contact-email-input" autoComplete="email" value={form.email} onChange={set("email")} placeholder={c.emailPh} />
                </div>
                <Field label={c.subject} data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} placeholder={c.subjectPh} />

                <label className="block">
                  <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[#5c5c57]">{c.message}</span>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={set("message")}
                    rows={4}
                    placeholder={c.messagePh}
                    className="mt-3 w-full resize-none border-0 border-b border-[#3a3a37] bg-transparent pb-3 font-display text-xl text-[#f4f4f0] outline-none transition-colors placeholder:text-[#5c5c57] focus:border-[#9e8d7b] md:text-2xl"
                  />
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-[#f4f4f0] px-9 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-[#1c1c1a] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {loading ? c.sending : c.send}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </button>

                <p className="font-body text-[11px] leading-relaxed text-[#5c5c57]" data-testid="contact-privacy-note">
                  {c.privacyBefore}
                  <Link to={ROUTES.privacy} className="underline underline-offset-2 hover:text-[#f4f4f0]">
                    {c.privacyLinkText}
                  </Link>
                  {c.privacyAfter}
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
